/**
 * @file Listener lifecycle ownership for the standalone Ordering domain.
 */

import {
    collection,
    doc,
    documentId,
    getDocs,
    limit,
    onSnapshot,
    orderBy,
    query,
    startAfter,
    where,
} from "https://www.gstatic.com/firebasejs/12.19.0/firebase-firestore.js";

import { db } from "../../app/firebase/firebase.js";
import { appData } from "../../app/state/state.js";
import { applyPendingOrderingMutationOverlays } from "./orderingMutations.js";
import { getActiveOrderingLists } from "./orderingLists.js";
import {
    ACTIVE_ITEM_LIMIT,
    CLOSED_PAGE_SIZE,
    getClosedBucketState,
    getOrderingState,
    invalidateOrderingClosedPages,
    isOrderingRouteGenerationCurrent,
    recordAuthoritativeOrderingItems,
    removeActiveItemsFromClosedPages,
} from "./orderingState.js";

const routeUnsubscribers = new Set();
let modalNoteUnsubscribe = null;

function runUnsubscribe(unsubscribe, label) {
    try {
        unsubscribe();
    } catch (error) {
        console.warn(`Error detaching Ordering ${label} listener:`, error);
    }
}

/**
 * Registers a route-scoped Ordering listener for navigation cleanup.
 * @param {Function} unsubscribe Firestore unsubscribe callback.
 * @returns {Function} Idempotent callback that detaches this listener.
 */
export function registerOrderingRouteListener(unsubscribe) {
    if (typeof unsubscribe !== "function") {
        throw new TypeError("Ordering route listeners require an unsubscribe function.");
    }

    let isDetached = false;
    const detach = () => {
        if (isDetached) {return;}
        isDetached = true;
        routeUnsubscribers.delete(detach);
        runUnsubscribe(unsubscribe, "route");
    };
    routeUnsubscribers.add(detach);
    return detach;
}

/**
 * Detaches every route-scoped Ordering listener.
 * @returns {void}
 */
export function detachOrderingRouteListeners() {
    Array.from(routeUnsubscribers).forEach((detach) => {
        detach();
    });
}

/**
 * Replaces the modal-scoped Ordering note listener.
 * @param {Function|null} unsubscribe Firestore unsubscribe callback or null.
 * @returns {void}
 */
export function setOrderingModalNoteListener(unsubscribe) {
    if (unsubscribe !== null && typeof unsubscribe !== "function") {
        throw new TypeError(
            "Ordering modal notes require an unsubscribe function or null.",
        );
    }

    detachOrderingModalNoteListener();
    modalNoteUnsubscribe = unsubscribe;
}

/**
 * Detaches the modal-scoped Ordering note listener.
 * @returns {void}
 */
export function detachOrderingModalNoteListener() {
    if (!modalNoteUnsubscribe) {return;}
    const unsubscribe = modalNoteUnsubscribe;
    modalNoteUnsubscribe = null;
    runUnsubscribe(unsubscribe, "modal note");
}

/**
 * Detaches all listener scopes owned by Ordering.
 * @returns {void}
 */
export function detachAllOrderingListeners() {
    detachOrderingModalNoteListener();
    detachOrderingRouteListeners();
}

function mapSnapshot(snapshot) {
    return snapshot.docs.map((entry) => ({id: entry.id, ...entry.data()}));
}

function syncConfiguredOrderingLists(config = appData.config) {
    const state = getOrderingState();
    state.buckets = getActiveOrderingLists(config);
    state.bucketLoadState = "ready";
    state.bucketError = null;
    return state.buckets;
}

/**
 * Ensures notification-opened modals have the complete active list set.
 * @param {number} generation Current route generation.
 * @returns {Promise<Array<object>>} Active buckets.
 */
export async function ensureOrderingBucketsReady(generation) {
    if (!isOrderingRouteGenerationCurrent(generation)) {return [];}
    return syncConfiguredOrderingLists();
}

/**
 * Attaches the route-scoped active-item listener.
 * @param {number} generation Current Ordering route generation.
 * @param {Function} onChange Render callback.
 * @returns {void}
 */
export function attachOrderingRouteListeners(generation, onChange) {
    detachOrderingRouteListeners();
    const state = getOrderingState();
    syncConfiguredOrderingLists();
    const activeItemsQuery = query(
        collection(db, "ordering_items"),
        where("isClosedResolved", "==", false),
        orderBy("updatedAt", "desc"),
        orderBy(documentId(), "desc"),
        limit(ACTIVE_ITEM_LIMIT),
    );

    registerOrderingRouteListener(onSnapshot(
        doc(db, "config", "main"),
        (snapshot) => {
            if (!isOrderingRouteGenerationCurrent(generation)) {return;}
            syncConfiguredOrderingLists(snapshot.exists() ? snapshot.data() : {});
            onChange();
        },
        (error) => {
            if (!isOrderingRouteGenerationCurrent(generation)) {return;}
            state.bucketLoadState = "error";
            state.bucketError = error;
            onChange();
        },
    ));

    registerOrderingRouteListener(onSnapshot(
        activeItemsQuery,
        (snapshot) => {
            if (!isOrderingRouteGenerationCurrent(generation)) {return;}
            const previousIds = new Set(state.activeItems.map((item) => item.id));
            const nextItems = mapSnapshot(snapshot);
            const nextIds = new Set(nextItems.map((item) => item.id));
            const activeMembershipRemoved = [...previousIds]
                .some((itemId) => !nextIds.has(itemId));
            const activeMembershipAdded = [...nextIds]
                .some((itemId) => !previousIds.has(itemId));
            if (activeMembershipRemoved || activeMembershipAdded) {
                invalidateOrderingClosedPages();
            }
            removeActiveItemsFromClosedPages(nextIds);
            state.activeItems = nextItems;
            recordAuthoritativeOrderingItems(state.activeItems);
            applyPendingOrderingMutationOverlays();
            state.activeLoadState = "ready";
            state.activeError = null;
            onChange();
        },
        (error) => {
            if (!isOrderingRouteGenerationCurrent(generation)) {return;}
            state.activeLoadState = "error";
            state.activeError = error;
            onChange();
        },
    ));
}

/**
 * Loads the next 30-item closed-history page for one active bucket.
 * @param {string} listId Ordering list ID.
 * @param {number} generation Current route generation.
 * @param {Function} onChange Render callback.
 * @returns {Promise<void>} Completion promise.
 */
export async function loadNextOrderingClosedPage(listId, generation, onChange) {
    const page = getClosedBucketState(listId);
    if (page.loading || !page.hasMore) {return;}
    const revision = page.revision;
    const loadToken = page.loadToken + 1;
    page.loadToken = loadToken;
    page.loading = true;
    page.error = null;
    onChange();

    const clauses = [
        where("listId", "==", listId),
        where("isClosedResolved", "==", true),
        orderBy("updatedAt", "desc"),
        orderBy(documentId(), "desc"),
    ];
    if (page.cursor) {
        clauses.push(startAfter(page.cursor.updatedAt, page.cursor.id));
    }
    clauses.push(limit(CLOSED_PAGE_SIZE));

    try {
        const snapshot = await getDocs(query(collection(db, "ordering_items"), ...clauses));
        if (!isOrderingRouteGenerationCurrent(generation) ||
            page.revision !== revision || page.loadToken !== loadToken) {return;}
        const items = mapSnapshot(snapshot);
        recordAuthoritativeOrderingItems(items);
        const knownIds = new Set(page.items.map((item) => item.id));
        page.items.push(...items.filter((item) => !knownIds.has(item.id)));
        const last = snapshot.docs.at(-1);
        page.cursor = last ? {updatedAt: last.get("updatedAt"), id: last.id} : page.cursor;
        page.hasMore = snapshot.size === CLOSED_PAGE_SIZE;
        page.loaded = true;
    } catch (error) {
        if (!isOrderingRouteGenerationCurrent(generation) ||
            page.revision !== revision || page.loadToken !== loadToken) {return;}
        page.error = error;
    } finally {
        if (isOrderingRouteGenerationCurrent(generation) &&
            page.revision === revision && page.loadToken === loadToken) {
            page.loading = false;
            onChange();
        }
    }
}
